/** @format */

import React from 'react';
import {InputWithErrorHandler, CheckboxWithErrorHandler, useForm} from 'frontier-form';
import ShapeSvg from './shape_svg';
import {
    SHAPE_OVAL,
    SHAPE_RECTANGLE,
    SHAPE_CARTON_AUTOLOCK,
    SHAPE_CARTON_STRAIGHT_TUCK,
    PRODUCT_TYPE_CARTON,
    PRODUCT_TYPE_LABEL
} from 'frontier-constants';
import {ConditionalButton} from 'frontier-utilities';
import DimensionValidator from 'frontier/utilities/dimension_validator';
import useSizeSpecs from './use_size_specs';
import useDownload from 'frontier/components/die_template/use_download';
import {svgLayers} from '../die_template_layout/layout';

export default function DieTemplateContainer(props) {
    const {
        updateFormData,
        onFormFloatNumberChange,
        formData: {width, length, depth, ...formData}
    } = useForm({
        width: 2,
        length: 4,
        depth: 3,
        shapeId: SHAPE_CARTON_AUTOLOCK,
        visibleLayers: svgLayers.map(l => l.id)
    });

    const {...sizeSpecs} = useSizeSpecs({
        width,
        length,
        depth,
        shapeId: formData.shapeId
    });

    const {downloadSvg, downloadDxf} = useDownload({
        width,
        length,
        depth,
        ...formData,
        ...sizeSpecs
    });

    const isCarton =
        formData.shapeId === SHAPE_CARTON_STRAIGHT_TUCK ||
        formData.shapeId === SHAPE_CARTON_AUTOLOCK;

    const dimensionValidator = new DimensionValidator({
        width,
        length,
        depth,
        shapeId: formData.shapeId,
        productTypeId: isCarton ? PRODUCT_TYPE_CARTON : PRODUCT_TYPE_LABEL
    });

    let isValid = dimensionValidator.isValidSize();

    return (
        <div
            style={{display: 'flex', flexWrap: 'wrap', minWidth: '500px'}}
            data-dom-reference="template generator starts">
            <div style={{padding: '20px', marginRight: '20px'}}>
                <h4>Shape</h4>
                <div>
                    <label>
                        <CheckboxWithErrorHandler
                            onChange={updateFormData.bind(this, {
                                shapeId: {$set: SHAPE_RECTANGLE},
                                isSquareCorners: {$set: false}
                            })}
                            checked={
                                formData.shapeId === SHAPE_RECTANGLE &&
                                formData.isSquareCorners === false
                            }
                        />{' '}
                        Square/Rectangle (with tw-rounded corners)
                    </label>
                </div>
                <div>
                    <label>
                        <CheckboxWithErrorHandler
                            onChange={updateFormData.bind(this, {
                                shapeId: {$set: SHAPE_RECTANGLE},
                                isSquareCorners: {$set: true}
                            })}
                            checked={
                                formData.shapeId === SHAPE_RECTANGLE &&
                                formData.isSquareCorners === true
                            }
                        />{' '}
                        Square/Rectangle (with square corners)
                    </label>
                </div>
                <div>
                    <label>
                        <CheckboxWithErrorHandler
                            onChange={updateFormData.bind(this, {
                                shapeId: {$set: SHAPE_OVAL},
                                isSquareCorners: {$set: false}
                            })}
                            checked={formData.shapeId === SHAPE_OVAL}
                        />{' '}
                        Circle/Oval
                    </label>
                </div>
                <div>
                    <label>
                        <CheckboxWithErrorHandler
                            onChange={updateFormData.bind(this, {
                                shapeId: {$set: SHAPE_CARTON_AUTOLOCK},
                                isSquareCorners: {$set: false}
                            })}
                            checked={formData.shapeId === SHAPE_CARTON_AUTOLOCK}
                        />{' '}
                        Carton (Auto Lock)
                    </label>
                </div>
                <div>
                    <label>
                        <CheckboxWithErrorHandler
                            onChange={updateFormData.bind(this, {
                                shapeId: {$set: SHAPE_CARTON_STRAIGHT_TUCK},
                                isSquareCorners: {$set: false}
                            })}
                            checked={formData.shapeId === SHAPE_CARTON_STRAIGHT_TUCK}
                        />{' '}
                        Carton (Straight Tuck)
                    </label>
                </div>
                <div>
                    <h4>Size</h4>

                    <div style={{display: 'flex'}}>
                        <div>
                            <label>Length</label>
                            <InputWithErrorHandler
                                style={{width: '150px'}}
                                className="form-control"
                                value={length}
                                type="number"
                                onChange={onFormFloatNumberChange.bind(this, 'length')}
                            />
                        </div>
                        <div>
                            <label htmlFor="width">Width</label>
                            <InputWithErrorHandler
                                style={{width: '150px'}}
                                className="form-control"
                                value={width}
                                type="number"
                                onChange={onFormFloatNumberChange.bind(this, 'width')}
                            />
                        </div>
                        {isCarton && (
                            <div>
                                <label>Depth</label>
                                <InputWithErrorHandler
                                    style={{width: '150px'}}
                                    className="form-control"
                                    value={depth}
                                    type="number"
                                    onChange={onFormFloatNumberChange.bind(this, 'depth')}
                                />
                            </div>
                        )}
                    </div>
                </div>
                {isCarton && (
                    <div className="mt-2">
                        <h4>Layer Visibility</h4>
                        {svgLayers.map((layer, index) => {
                            return (
                                <div className="d-flex">
                                    <div>
                                        <label>
                                            <CheckboxWithErrorHandler
                                                onChange={updateFormData.bind(this, {
                                                    visibleLayers: {
                                                        $apply: layers => {
                                                            if (layers.indexOf(layer.id) > -1) {
                                                                return layers.filter(
                                                                    l => l !== layer.id
                                                                );
                                                            } else {
                                                                return layers.concat(layer.id);
                                                            }
                                                        }
                                                    }
                                                })}
                                                checked={
                                                    formData.visibleLayers.indexOf(layer.id) > -1
                                                }
                                            />{' '}
                                            {layer.label}
                                        </label>
                                    </div>
                                    <div className="ml-2">{layer.description}</div>
                                </div>
                            );
                        })}
                    </div>
                )}
                {dimensionValidator.getErrors().length > 0 && (
                    <div className="alert alert-danger">
                        {dimensionValidator.getErrors().map((e, index) => {
                            return <div key={index}>{e}</div>;
                        })}
                    </div>
                )}
                <div className="mt-2">
                    <ConditionalButton enabled={!isValid}>
                        <button className="btn btn-outline-secondary" disabled="disabled">
                            Download
                        </button>
                        <button
                            className="btn btn-outline-secondary"
                            onClick={downloadSvg}
                            disabled={props.error}>
                            Download
                        </button>
                    </ConditionalButton>

                    {isCarton && (
                        <ConditionalButton enabled={!isValid}>
                            <button
                                className="btn btn-outline-secondary tw-ml-4"
                                disabled="disabled">
                                Download DXF
                            </button>
                            <button
                                className="btn btn-outline-secondary tw-ml-4"
                                onClick={downloadDxf}
                                disabled={props.error}>
                                Download DXF
                            </button>
                        </ConditionalButton>
                    )}
                </div>
            </div>
            <div style={{padding: '20px'}}>
                {isValid && (
                    <div
                        style={{
                            minWidth: '500px',
                            maxHeight: '700px',
                            width: '100%',
                            height: '100%'
                        }}>
                        <ShapeSvg
                            width={width}
                            length={length}
                            depth={depth}
                            {...formData}
                            {...sizeSpecs}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
